import { includes, map, range } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RoleType, RoleTypes } from "@/shared/constants";
import { DropdownMenu, DropdownMenuProps } from "@/shared/widgets/DropdownMenu";
import { SelectButton } from "@/shared/ui/SelectButton";

interface RoleSelectDropdownProps
  extends Omit<
    DropdownMenuProps,
    "trigger" | "items" | "onSelect" | "children"
  > {
  value?: RoleType;
  placeholder?: string;
  onSelect: (role: RoleType) => void | Promise<boolean | void>;
  filter?: RoleType[];
  className?: string;
}

function RoleSelectDropdown(props: RoleSelectDropdownProps) {
  const {
    value: externalValue,
    placeholder,
    filter: filterRoles = [],
    onSelect,
    className,
    ...rest
  } = props;
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState<RoleType | undefined>(
    externalValue
  );

  const getRoleLabel = (role?: RoleType) => {
    if (!role) return undefined;
    switch (role) {
      case RoleType.owner:
        return t("common.owner");
      case RoleType.admin:
        return t("common.admin");
      case RoleType.member:
        return t("common.member");
      case RoleType.candidate:
        return t("common.candidate");
      case RoleType.contact:
        return t("common.contact");
      case RoleType.collaborator:
        return t("common.collaborator");
      default:
        return undefined;
    }
  };

  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  const handleSelect = async (roleKey: RoleType) => {
    const previousValue = internalValue;
    setInternalValue(roleKey);

    const result = await onSelect(roleKey);

    // If onSelect returns false, revert to previous value
    if (result === false) {
      setInternalValue(previousValue);
    }
  };

  const options = map(RoleTypes, (role) => ({
    label: getRoleLabel(role.key as RoleType) || role.name,
    value: role.key,
    onClick: () => handleSelect(role.key as RoleType),
    selected: role.key === internalValue,
  })).filter((option) =>
    filterRoles?.length ? includes(filterRoles, option.value) : true
  );

  return (
    <DropdownMenu
      {...rest}
      items={options}
      separatorIndexes={range(1, options.length)}
      trigger={
        <SelectButton
          size="sm"
          value={getRoleLabel(internalValue)}
          placeholder={placeholder || t("common.select")}
          className={className}
        />
      }
      side="bottom"
      align="end"
    />
  );
}

export { RoleSelectDropdown };
